import { z } from "zod";
import { cents, csv, dateStr, flag, id, limit, localDateTime, nonNegCents, nullableText, pct } from "./common";
import { contactInput } from "./contacts";
import { scheduleV1 } from "./schedule";

export const bookingStatus = z.enum(["pending", "contract_sent", "signed", "booked", "completed", "cancelled"]);
export type BookingStatus = z.infer<typeof bookingStatus>;
export const lineKind = z.enum(["package", "fee", "addon", "discount", "processing_fee", "late_fee", "extra"]);
export type LineKind = z.infer<typeof lineKind>;
export const damageMode = z.enum(["collect", "hold"]);

const eventFields = {
  eventDate: dateStr.optional(),
  endDate: dateStr.optional(),
  setupStartsAt: localDateTime.nullable().optional(),
  startsAt: localDateTime.nullable().optional(),
  endsAt: localDateTime.nullable().optional(),
  vacateAt: localDateTime.nullable().optional(),
  eventTypeId: id.nullable().optional(),
  eventName: z.string().trim().max(200).nullable().optional(),
  guestCount: z.number().int().min(0).nullable().optional(),
  guestCountMax: z.number().int().min(0).nullable().optional(),
  notesClient: nullableText.optional(),
  notesInternal: nullableText.optional(),
  clientIsCompany: flag.optional(),
  clientCompanyName: z.string().trim().max(200).nullable().optional(),
  secondaryContactId: id.nullable().optional(),
  assignedMemberId: id.nullable().optional(),
  bookerMemberId: id.nullable().optional(),
  damageOn: flag.optional(),
  damageAmount: nonNegCents.optional(),
  damageMode: damageMode.optional(),
};

export const bookingCreate = z
  .object({
    clientRequestId: z.uuid().transform(value => value.toLowerCase()).optional(),
    venueId: id,
    contactId: id.optional(),
    contact: contactInput.optional(),
    leadId: id.optional(),
    packageCatalogItemId: id.optional(),
    packageQty: z.number().int().min(1).optional(),
    schedule: scheduleV1.optional(),
    ...eventFields,
  })
  .refine((b) => b.schedule || b.eventDate || b.startsAt, { message: "An event start date is required", path: ["schedule", "eventStart", "date"] });
export type BookingCreate = z.infer<typeof bookingCreate>;

export const bookingPatch = z.object({
  venueId: id.optional(), contactId: id.nullable().optional(), ...eventFields,
  schedule: scheduleV1.optional(),
  expectedScheduleRevision: z.number().int().min(0).optional(),
});
export type BookingPatch = z.infer<typeof bookingPatch>;

export const bookingCancel = z.object({ reason: z.string().trim().min(1).max(500) });

export const scheduleOverride = z
  .object({
    depositPct: pct.optional(),
    depositAmount: nonNegCents.optional(),
    depositDueDate: dateStr.optional(),
    balanceDueDate: dateStr.optional(),
    payInFull: flag.optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: "nothing to override" });
export type ScheduleOverride = z.infer<typeof scheduleOverride>;

export const lineAdd = z
  .object({
    catalogItemId: id.optional(),
    confirmPayInFull: z.boolean().optional(),
    kind: lineKind.optional(),
    name: z.string().trim().min(1).max(200).optional(),
    qty: z.number().int().min(1).optional(),
    unitAmount: cents.optional(),
    pct: pct.optional(),
    note: nullableText.optional(),
    onContract: flag.optional(),
  })
  .refine((l) => l.catalogItemId || (l.kind && l.name), { message: "catalogItemId or kind + name is required", path: ["kind"] });
export type LineAdd = z.infer<typeof lineAdd>;

export const linePatch = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  qty: z.number().int().min(1).optional(),
  unitAmount: cents.optional(),
  pct: pct.optional(),
  note: nullableText.optional(),
  onContract: flag.optional(),
  sort: z.number().int().optional(),
});
export type LinePatch = z.infer<typeof linePatch>;

export const bookingListQuery = z.object({
  reviewStatus: z.enum(["unreviewed", "reviewed", "approved"]).optional(),
  awaitingCountersign: z.enum(["1", "0"]).optional(),
  status: csv.optional(),
  venueId: id.optional(),
  q: z.string().trim().optional(),
  from: dateStr.optional(),
  to: dateStr.optional(),
  limit,
  cursor: z.string().optional(),
});
export type BookingListQuery = z.infer<typeof bookingListQuery>;
